import * as React from "react";
import { useQuery } from "react-query";
import { Typography, Box, TablePagination, Stack } from "@mui/material";
import { DataTable } from "../../../../components/DataTable/DataTable";
import { ApiErrorMessage } from "../../../../components/ApiErrorMessage/ApiErrorMessage";
import { ExportButton } from "../../../../components/ExportButton/ExportButton";
import { MetricsService } from "../../../../gen/clients";
import { default as dayjs } from "dayjs";

const MT_DOC_TRANSLATION_EVENT_TYPE = "MT_DOC_TRANSLATION_REQUESTED";
const MT_DOC_ESCALATION_TO_HUMAN_EVENT_TYPE = "MT_DOC_ESCALATION_TO_HUMAN";
const MT_MEDIA_TRANSLATION_EVENT_TYPE = "MT_MEDIA_TRANSLATION_REQUESTED";
const MT_MEDIA_ESCALATION_TO_HUMAN_EVENT_TYPE = "MT_MEDIA_ESCALATION_TO_HUMAN";

interface EscalationTableRow {
  clientId: string;
  clientName: string;
  branchId: string;
  docReq: number;
  docEsc: number;
  mediaReq: number;
  mediaEsc: number;
  [key: string]: string | number;
}

interface ExportedDataRow {
  clientName: string;
  clientId: string;
  branchId: string;
  docReq: number;
  docEsc: number;
  mediaReq: number;
  mediaEsc: number;
  [key: string]: string | number;
}

const columnHeaders: Record<keyof ExportedDataRow, string> = {
  clientName: "Client Name",
  clientId: "Client ID",
  branchId: "Branch ID",
  docReq: "Request Count (Documents)",
  docEsc: "Escalation Count (Documents)",
  mediaReq: "Request Count (Multimedia)",
  mediaEsc: "Escalation Count (Multimedia)"
};

const buildQuery = (fromISO: string, toISO: string): Record<string, any> => ({
  size: 0,
  query: {
    bool: {
      filter: [
        {
          range: {
            "@timestamp": {
              gte: fromISO,
              lt: toISO
            }
          }
        },
        {
          terms: {
            eventType: [
              MT_DOC_TRANSLATION_EVENT_TYPE,
              MT_DOC_ESCALATION_TO_HUMAN_EVENT_TYPE,
              MT_MEDIA_TRANSLATION_EVENT_TYPE,
              MT_MEDIA_ESCALATION_TO_HUMAN_EVENT_TYPE
            ]
          }
        }
      ]
    }
  },
  aggs: {
    by_client: {
      terms: {
        field: "clientId",
        size: 10000
      },
      aggs: {
        by_branch: {
          terms: {
            field: "branchId",
            size: 10000,
            missing: "UNKNOWN"
          },
          aggs: {
            client_name: {
              top_hits: {
                _source: ["clientName", "clientId", "branchId"],
                size: 1
              }
            },
            by_type: {
              filters: {
                filters: {
                  doc_req: {
                    term: {
                      eventType: MT_DOC_TRANSLATION_EVENT_TYPE
                    }
                  },
                  doc_esc: {
                    term: {
                      eventType: MT_DOC_ESCALATION_TO_HUMAN_EVENT_TYPE
                    }
                  },
                  media_req: {
                    term: {
                      eventType: MT_MEDIA_TRANSLATION_EVENT_TYPE
                    }
                  },
                  media_esc: {
                    term: {
                      eventType: MT_MEDIA_ESCALATION_TO_HUMAN_EVENT_TYPE
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
});

const parseResponse = (response: any): EscalationTableRow[] => {
  const clientBuckets = response?.aggregations?.by_client?.buckets || [];

  const rows = clientBuckets.flatMap((clientBucket: any) => {
    const clientId = clientBucket.key;
    const branchBuckets = clientBucket.by_branch?.buckets || [];

    return branchBuckets.map((branchBucket: any) => {
      const branchId = branchBucket.key === "UNKNOWN" ? "—" : branchBucket.key;
      const clientName = branchBucket.client_name?.hits?.hits?.[0]?._source?.clientName || "";
      const typeBuckets = branchBucket.by_type?.buckets || {};

      return {
        clientId,
        clientName,
        branchId,
        docReq: typeBuckets.doc_req?.doc_count || 0,
        docEsc: typeBuckets.doc_esc?.doc_count || 0,
        mediaReq: typeBuckets.media_req?.doc_count || 0,
        mediaEsc: typeBuckets.media_esc?.doc_count || 0
      };
    });
  });

  // Sort by total escalations descending
  return rows.sort((a: EscalationTableRow, b: EscalationTableRow) => b.docEsc + b.mediaEsc - (a.docEsc + a.mediaEsc));
};

interface Props {
  from: Date;
  to: Date;
  refetchRef?: React.MutableRefObject<() => void>;
}

const EscalationDetailsTable: React.FC<Props> = ({ from, to, refetchRef }) => {
  const fromISO = React.useMemo(() => from.toISOString(), [from]);
  const toISO = React.useMemo(() => to.toISOString(), [to]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const { data, isLoading, error, refetch } = useQuery(
    ["mt-escalation-table", fromISO, toISO],
    () =>
      MetricsService.searchIndex({
        indexName: "app-events-v2",
        requestBody: buildQuery(fromISO, toISO)
      }),
    {
      cacheTime: 0
    }
  );

  React.useEffect(() => {
    if (refetchRef) {
      refetchRef.current = refetch;
    }
  }, [refetch, refetchRef]);

  const rows = React.useMemo(() => parseResponse(data), [data]);

  const pageData = React.useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return rows.slice(startIndex, endIndex);
  }, [rows, page, rowsPerPage]);

  const getExportData = React.useCallback(async (): Promise<ExportedDataRow[]> => {
    return rows;
  }, [rows]);

  const fileName = `mt-escalations-${dayjs(from).format("MM-DD-YYYY")}-${dayjs(to).format("MM-DD-YYYY")}.csv`;

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2
        }}
      >
        <Typography variant="h6">Escalation Details by Client</Typography>
        <ExportButton<ExportedDataRow>
          filename={fileName}
          columnHeaders={columnHeaders}
          getExportData={getExportData}
          exportingMessage="Exporting escalation data..."
        />
      </Box>

      {error ? (
        <ApiErrorMessage apiError={error} />
      ) : (
        <Stack>
          <DataTable
            columns={[
              {
                id: "clientName",
                title: "Client Name",
                cell: (row: EscalationTableRow) => row.clientName
              },
              {
                id: "clientId",
                title: "Client ID",
                cell: (row: EscalationTableRow) => row.clientId
              },
              {
                id: "branchId",
                title: "Branch ID",
                cell: (row: EscalationTableRow) => row.branchId
              },
              {
                id: "docReq",
                title: "Request Count (Documents)",
                cell: (row: EscalationTableRow) => row.docReq
              },
              {
                id: "docEsc",
                title: "Escalation Count (Documents)",
                cell: (row: EscalationTableRow) => row.docEsc
              },
              {
                id: "mediaReq",
                title: "Request Count (Multimedia)",
                cell: (row: EscalationTableRow) => row.mediaReq
              },
              {
                id: "mediaEsc",
                title: "Escalation Count (Multimedia)",
                cell: (row: EscalationTableRow) => row.mediaEsc
              }
            ]}
            data={pageData}
            rowKey={(row: EscalationTableRow) => `${row.clientId}:${row.branchId}`}
            isLoading={isLoading}
            noData={<Typography>No escalation data found for the selected date range.</Typography>}
          />
          <TablePagination
            count={rows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => {
              setRowsPerPage(+e.target.value);
              setPage(0);
            }}
            component="div"
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Stack>
      )}
    </Box>
  );
};

export { EscalationDetailsTable };
